import AlimTalkLedger from "./AlimTalkLedger";
import { ListDateFilters, ListPagination } from "./NotificationListControls";
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";

import { useAuthContext } from "../../../Components/AuthContext";
import {
  Alert,
  Badge,
  Button,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "../../../Components/ui";
import {
  AlimTalkAccountMode,
  AlimTalkAccountResponse,
  AlimTalkBillingSetting,
  AlimTalkMessageRate,
  AlimTalkTopupRequest,
  AlimTalkWallet,
  notificationCenterService,
} from "../../../api/services/notification-center.service";
import { hasCapability } from "../../../common/permissions";
import { dialogService } from "../../../utils/dialogService";
import { readWalletAction, saveWalletAction, clearWalletAction, walletActionStorageKey, isDefinitiveWalletFailure } from "./pendingWalletAction";
import AlimTalkAccountSettings from "./AlimTalkAccountSettings";
import { normalizeAlimTalkAccountMode } from "./alimtalkTemplate";

const asTopupList = (data: { results?: AlimTalkTopupRequest[] } | AlimTalkTopupRequest[] | undefined) => (
  Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : []
);

const asRateList = (data: { results?: AlimTalkMessageRate[] } | AlimTalkMessageRate[] | undefined) => (
  Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : []
);

const localDateValue = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
};

const amountOf = (request: AlimTalkTopupRequest) => Number(
  request.approved_amount ?? request.requested_amount ?? request.amount ?? 0,
);

const formatMoney = (value?: number | string | null, currency = "KRW") => {
  const amount = Number(value || 0);
  return currency === "KRW"
    ? `${amount.toLocaleString("ko-KR")}원`
    : `${amount.toLocaleString("ko-KR")} ${currency}`;
};

const statusMeta = (status: string) => {
  const normalized = String(status || "").toLowerCase();
  const mapping: Record<string, { label: string; color: string }> = {
    pending: { label: "입금 확인 대기", color: "warning" },
    approved: { label: "충전 완료", color: "success" },
    rejected: { label: "반려", color: "danger" },
    cancelled: { label: "취소", color: "secondary" },
  };
  return mapping[normalized] || { label: status || "상태 미확인", color: "secondary" };
};

const errorMessage = (error: any, fallback: string) => (
  error?.response?.data?.detail || error?.response?.data?.error || error?.message || fallback
);

const createWalletAdjustmentIdempotencyKey = () => {
  const token = globalThis.crypto?.randomUUID?.()
    || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `crm-alimtalk-wallet-adjustment:${token}`;
};

type ReviewState = {
  request: AlimTalkTopupRequest;
  action: "approve" | "reject";
} | null;

type AlimTalkChannelSettingsProps = {
  view?: "site" | "headquarters";
  onAccountModeChange?: (mode: AlimTalkAccountMode | null) => void;
};

export default function AlimTalkChannelSettings({
  view = "site",
  onAccountModeChange,
}: AlimTalkChannelSettingsProps) {
  const { userInfo } = useAuthContext();
  const [account, setAccount] = useState<AlimTalkAccountResponse | null>(null);
  const [wallet, setWallet] = useState<AlimTalkWallet | null>(null);
  const [billingSetting, setBillingSetting] = useState<AlimTalkBillingSetting | null>(null);
  const [adminBillingSetting, setAdminBillingSetting] = useState<AlimTalkBillingSetting | null>(null);
  const [topups, setTopups] = useState<AlimTalkTopupRequest[]>([]);
  const [adminTopupsUnavailable, setAdminTopupsUnavailable] = useState(false);
  const [topupPending, setTopupPending] = useState(false);
  const [adjustmentPending, setAdjustmentPending] = useState(false);
  const mutationLock = useRef(false);
  const [cancellingTopup, setCancellingTopup] = useState(false);
  const [adminTopups, setAdminTopups] = useState<AlimTalkTopupRequest[]>([]);
  const [adminRates, setAdminRates] = useState<AlimTalkMessageRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [walletUnavailable, setWalletUnavailable] = useState(false);
  const [topupsUnavailable, setTopupsUnavailable] = useState(false);
  const [adminBillingUnavailable, setAdminBillingUnavailable] = useState(false);
  const [adminRatesUnavailable, setAdminRatesUnavailable] = useState(false);
  const [changingMode, setChangingMode] = useState(false);
  const [topupOpen, setTopupOpen] = useState(false);
  const [topupAmount, setTopupAmount] = useState("");
  const [depositorName, setDepositorName] = useState("");
  const [expectedDepositDate, setExpectedDepositDate] = useState("");
  const [topupNote, setTopupNote] = useState("");
  const [submittingTopup, setSubmittingTopup] = useState(false);
  const [review, setReview] = useState<ReviewState>(null);
  const [reviewAmount, setReviewAmount] = useState("");
  const [reviewMemo, setReviewMemo] = useState("");
  const [reviewing, setReviewing] = useState(false);
  const [savingAdminBilling, setSavingAdminBilling] = useState(false);
  const [rateScope, setRateScope] = useState<"GLOBAL" | "SITE">("GLOBAL");
  const [rateChannel, setRateChannel] = useState<"AT" | "AI">("AT");
  const [rateUnitPrice, setRateUnitPrice] = useState("");
  const [rateEffectiveFrom, setRateEffectiveFrom] = useState(localDateValue);
  const [rateReason, setRateReason] = useState("");
  const [savingAdminRate, setSavingAdminRate] = useState(false);
  const [walletAdjustmentOpen, setWalletAdjustmentOpen] = useState(false);
  const [walletAdjustmentAmount, setWalletAdjustmentAmount] = useState("");
  const [walletAdjustmentReason, setWalletAdjustmentReason] = useState("");
  const [walletAdjustmentIdempotencyKey, setWalletAdjustmentIdempotencyKey] = useState("");
  const [adjustingWallet, setAdjustingWallet] = useState(false);

  const isGlobalAdmin = Boolean(userInfo?.is_global_admin);
  const isHeadquartersView = view === "headquarters";
  const canManageHeadquarters = isHeadquartersView && userInfo?.is_hq_admin === true;
  const isSiteOwner = hasCapability(userInfo, "site_owner");
  const isSiteManager = hasCapability(userInfo, "site_manager");
  const canManageSiteWallet = isGlobalAdmin || isSiteOwner;

  const [topupQuery, setTopupQuery] = useState({ page: 1, status: view === "headquarters" ? "PENDING" : "", search: "", created_from: "", created_to: "" });
  const [topupCount, setTopupCount] = useState(0);
  const [loadingTopups, setLoadingTopups] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const load = useCallback(async () => {
    setLoading(true);
    setWalletUnavailable(false);
    setAdminBillingUnavailable(false);
    setAdminRatesUnavailable(false);
    try {
      const accountResponse = await notificationCenterService.getAlimTalkAccount();
      const nextAccount = accountResponse.data;
      setAccount(nextAccount);
      setWallet(nextAccount.wallet || null);

      const mode = normalizeAlimTalkAccountMode(nextAccount.account_mode, nextAccount.using_default);
      onAccountModeChange?.(mode);
      if (!isHeadquartersView && mode === "HEADQUARTERS" && canManageSiteWallet) {
        const [walletResult] = await Promise.allSettled([
          notificationCenterService.getAlimTalkWallet(),
        ]);
        if (walletResult.status === "fulfilled") {
          setWallet(walletResult.value.data.wallet || walletResult.value.data);
          setBillingSetting(walletResult.value.data.billing_setting || null);
        }
        else setWalletUnavailable(!nextAccount.wallet);
      } else {
        setTopups([]);
        setBillingSetting(null);
      }
    } catch (error: any) {
      setAccount(null);
      onAccountModeChange?.(null);
      toast.error(errorMessage(error, "알림톡 채널 정보를 불러오지 못했습니다."));
    }

    if (canManageHeadquarters) {
      const [billingResult, ratesResult] = await Promise.allSettled([
        notificationCenterService.getAdminAlimTalkBillingSetting(),
        notificationCenterService.getAdminAlimTalkMessageRates({ page_size: 200 }),
      ]);
      if (billingResult.status === "fulfilled") {
        setAdminBillingSetting(billingResult.value.data);
        setBillingSetting(billingResult.value.data);
      } else {
        setAdminBillingSetting(null);
        setAdminBillingUnavailable(true);
      }
      if (ratesResult.status === "fulfilled") {
        setAdminRates(
          asRateList(ratesResult.value.data).filter((rate) => (
            rate.channel === "AT" || rate.channel === "AI"
          )),
        );
      } else {
        setAdminRates([]);
        setAdminRatesUnavailable(true);
      }
    } else {
      setAdminTopups([]);
      setAdminBillingSetting(null);
      setAdminRates([]);
    }
    setLoading(false);
    setRefreshKey((value) => value + 1);
  }, [canManageHeadquarters, canManageSiteWallet, isHeadquartersView, onAccountModeChange]);

  useEffect(() => {
    void load();
  }, [load]);

  const showSiteTopups = !isHeadquartersView && canManageSiteWallet && Boolean(account && normalizeAlimTalkAccountMode(account.account_mode, account.using_default) === "HEADQUARTERS");
  useEffect(() => {
    let active = true;
    if (loading || (!canManageHeadquarters && !showSiteTopups)) return;
    setLoadingTopups(true); setTopups([]); setAdminTopups([]);
    setTopupsUnavailable(false); setAdminTopupsUnavailable(false);
    const fetch = canManageHeadquarters ? notificationCenterService.getAdminAlimTalkTopupRequests : notificationCenterService.getAlimTalkTopupRequests;
    void fetch({ ...topupQuery, page_size: 20 }).then(({ data }) => {
      if (!active) return;
      const rows = asTopupList(data);
      if (canManageHeadquarters) setAdminTopups(rows); else setTopups(rows);
      setTopupCount(Array.isArray(data) ? data.length : Number(data.count ?? rows.length));
    }).catch((error) => {
      if (!active) return;
      if (error?.response?.status === 404 && topupQuery.page > 1) {
        setTopupQuery((query) => ({ ...query, page: query.page - 1 }));
        return;
      }
      setTopupCount(0);
      if (canManageHeadquarters) setAdminTopupsUnavailable(true); else setTopupsUnavailable(true);
    }).finally(() => { if (active) setLoadingTopups(false); });
    return () => { active = false; };
  }, [canManageHeadquarters, showSiteTopups, topupQuery, refreshKey, loading]);

  const topupStorageKey = walletActionStorageKey(userInfo?.id, account?.site_id, "topup");
  const adjustmentStorageKey = walletActionStorageKey(userInfo?.id, account?.site_id, "adjustment");
  useEffect(() => {
    const topup = readWalletAction(topupStorageKey);
    setTopupPending(Boolean(topup));
    if (topup) {
      setTopupAmount(String(topup.payload.requested_amount)); setDepositorName(String(topup.payload.depositor_name));
      setExpectedDepositDate(String(topup.payload.expected_deposit_date || "")); setTopupNote(String(topup.payload.memo || ""));
    }
    const adjustment = readWalletAction(adjustmentStorageKey);
    setAdjustmentPending(Boolean(adjustment));
    if (adjustment) {
      setWalletAdjustmentAmount(String(adjustment.payload.amount)); setWalletAdjustmentReason(String(adjustment.payload.reason)); setWalletAdjustmentIdempotencyKey(adjustment.key);
    }
  }, [topupStorageKey, adjustmentStorageKey]);
  const mode = normalizeAlimTalkAccountMode(account?.account_mode, account?.using_default);
  const canChangeMode = canManageHeadquarters;
  const canEditConnection = !isHeadquartersView && canManageSiteWallet;
  const canRequestTopup = Boolean(
    !isHeadquartersView
    && (account?.capabilities?.can_request_topup ?? true)
    && canManageSiteWallet,
  );
  const pendingTopup = Number(wallet?.pending_topup_count || 0) > 0 || topups.some((request) => String(request.status).toLowerCase() === "pending");
  const topupFilters = <div className="d-flex flex-wrap gap-2 mb-3">
    <Input aria-label="충전 상태" type="select" value={topupQuery.status} onChange={(event) => setTopupQuery({ ...topupQuery, page: 1, status: event.target.value })} style={{ maxWidth: 150 }}><option value="">전체 상태</option><option value="PENDING">입금 대기</option><option value="APPROVED">승인 완료</option><option value="REJECTED">반려</option><option value="CANCELLED">취소</option></Input>
    <Input aria-label="충전 검색" placeholder="지점, 입금자, 요청 번호 검색" value={topupQuery.search} onChange={(event) => setTopupQuery({ ...topupQuery, page: 1, search: event.target.value })} style={{ maxWidth: 260 }} />
    <ListDateFilters from={topupQuery.created_from} to={topupQuery.created_to} onChange={(created_from, created_to) => setTopupQuery({ ...topupQuery, page: 1, created_from, created_to })} />
  </div>;
  const topupPagination = <ListPagination label="충전 내역" page={topupQuery.page} count={topupCount} loading={loadingTopups} onChange={(page) => setTopupQuery({ ...topupQuery, page })} />;

  const changeMode = async (nextMode: AlimTalkAccountMode) => {
    if (!canManageHeadquarters || !account || nextMode === mode || changingMode || mutationLock.current || adjustmentPending || topupPending) return;
    const confirmed = await dialogService.confirm({
      title: nextMode === "HEADQUARTERS" ? "본사 공용 계정을 사용하시겠습니까?" : "지점 직접 계정으로 전환하시겠습니까?",
      description: nextMode === "HEADQUARTERS"
        ? "본사 공용 발신 프로필과 템플릿을 사용하며 알림톡 발송 시 지점 충전금이 차감됩니다."
        : "지점에서 발급받은 발신 프로필을 연결해야 알림톡을 발송할 수 있습니다.",
      confirmLabel: "전환",
      cancelLabel: "취소",
    });
    if (!confirmed || mutationLock.current) return;
    mutationLock.current = true;
    setChangingMode(true);
    try {
      const response = await notificationCenterService.updateAlimTalkAccount({ account_mode: nextMode });
      setAccount(response.data);
      onAccountModeChange?.(normalizeAlimTalkAccountMode(response.data.account_mode, response.data.using_default));
      toast.success("알림톡 계정 사용 방식을 변경했습니다.");
      await load();
    } catch (error: any) {
      toast.error(errorMessage(error, "알림톡 계정 사용 방식을 변경하지 못했습니다."));
    } finally {
      mutationLock.current = false;
      setChangingMode(false);
    }
  };

  const submitTopup = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (mutationLock.current || !account) return;
    const amount = Number(topupAmount);
    if (!Number.isInteger(amount) || amount <= 0 || !depositorName.trim()) {
      toast.error("충전 요청 금액과 입금자명을 확인해 주세요.");
      return;
    }
    mutationLock.current = true;
    setSubmittingTopup(true);
    try {
      const action = readWalletAction(topupStorageKey) || { key: `crm-topup:${globalThis.crypto.randomUUID()}`, payload: {
        requested_amount: amount,
        depositor_name: depositorName.trim(),
        expected_deposit_date: expectedDepositDate || undefined,
        memo: topupNote.trim(),
      } };
      saveWalletAction(topupStorageKey, action);
      setTopupPending(true);
      await notificationCenterService.createAlimTalkTopupRequest(action.payload as { requested_amount: number; depositor_name: string }, action.key);
      clearWalletAction(topupStorageKey);
      setTopupPending(false);
      toast.success("충전 요청을 등록했습니다. 입금 확인 후 충전금에 반영됩니다.");
      setTopupOpen(false);
      setTopupAmount("");
      setDepositorName("");
      setExpectedDepositDate("");
      setTopupNote("");
      await load();
    } catch (error: any) {
      if (isDefinitiveWalletFailure(error)) { clearWalletAction(topupStorageKey); setTopupPending(false); }
      toast.error(errorMessage(error, "충전 요청을 등록하지 못했습니다."));
    } finally {
      mutationLock.current = false;
      setSubmittingTopup(false);
    }
  };

  const cancelTopup = async (request: AlimTalkTopupRequest) => {
    if (mutationLock.current) return;
    const confirmed = await dialogService.confirm("대기 중인 충전 요청을 취소하시겠습니까?");
    if (!confirmed || mutationLock.current) return;
    mutationLock.current = true;
    setCancellingTopup(true);
    try {
      await notificationCenterService.cancelAlimTalkTopupRequest(request.id);
      toast.success("충전 요청을 취소했습니다.");
      await load();
    } catch (error: any) {
      toast.error(errorMessage(error, "충전 요청을 취소하지 못했습니다."));
    } finally { mutationLock.current = false; setCancellingTopup(false); }
  };

  const openReview = (request: AlimTalkTopupRequest, action: "approve" | "reject") => {
    if (!canManageHeadquarters) return;
    setReview({ request, action });
    setReviewAmount(String(amountOf(request) || ""));
    setReviewMemo("");
  };

  const submitReview = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canManageHeadquarters || !review || mutationLock.current) return;
    if (review.action === "approve" && (!Number.isInteger(Number(reviewAmount)) || Number(reviewAmount) <= 0)) {
      toast.error("실제 입금 확인 금액을 입력해 주세요.");
      return;
    }
    if (review.action === "reject" && !reviewMemo.trim()) {
      toast.error("반려 사유를 입력해 주세요.");
      return;
    }
    if (
      review.action === "approve"
      && Number(reviewAmount) !== amountOf(review.request)
      && !reviewMemo.trim()
    ) {
      toast.error("요청 금액과 다른 확인 금액에는 확인 메모를 입력해 주세요.");
      return;
    }
    mutationLock.current = true;
    setReviewing(true);
    try {
      if (review.action === "approve") {
        await notificationCenterService.approveAdminAlimTalkTopupRequest(review.request.id, {
          approved_amount: Number(reviewAmount),
          memo: reviewMemo.trim(),
        });
        toast.success("입금 확인 금액을 충전금에 반영했습니다.");
      } else {
        await notificationCenterService.rejectAdminAlimTalkTopupRequest(review.request.id, { reason: reviewMemo.trim() });
        toast.success("충전 요청을 반려했습니다.");
      }
      setReview(null);
      await load();
    } catch (error: any) {
      toast.error(errorMessage(error, "충전 요청을 처리하지 못했습니다."));
    } finally {
      mutationLock.current = false;
      setReviewing(false);
    }
  };

  const updateAdminBillingField = <K extends keyof AlimTalkBillingSetting>(
    field: K,
    value: AlimTalkBillingSetting[K],
  ) => {
    setAdminBillingSetting((current) => ({ ...(current || {}), [field]: value }));
  };

  const submitAdminBilling = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canManageHeadquarters || !adminBillingSetting) return;
    const bankName = String(adminBillingSetting.bank_name || "").trim();
    const accountNumber = String(adminBillingSetting.account_number || "").trim();
    const accountHolder = String(adminBillingSetting.account_holder || "").trim();
    setSavingAdminBilling(true);
    try {
      const response = await notificationCenterService.updateAdminAlimTalkBillingSetting({
        bank_name: bankName,
        account_number: accountNumber,
        account_holder: accountHolder,
        guidance: String(adminBillingSetting.guidance || "").trim(),
        enforcement_enabled: Boolean(adminBillingSetting.enforcement_enabled),
      });
      setAdminBillingSetting(response.data);
      setBillingSetting(response.data);
      toast.success("본사 알림톡 청구 설정을 저장했습니다.");
    } catch (error: any) {
      toast.error(errorMessage(error, "본사 알림톡 청구 설정을 저장하지 못했습니다."));
    } finally {
      setSavingAdminBilling(false);
    }
  };

  const submitWalletAdjustment = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canManageHeadquarters || !account || mutationLock.current) return;
    const amount = Number(walletAdjustmentAmount);
    if (!Number.isInteger(amount) || amount === 0 || !walletAdjustmentReason.trim()) {
      toast.error("0원이 아닌 정수 조정 금액과 사유를 입력해 주세요.");
      return;
    }
    const idempotencyKey = walletAdjustmentIdempotencyKey || createWalletAdjustmentIdempotencyKey();
    if (!walletAdjustmentIdempotencyKey) setWalletAdjustmentIdempotencyKey(idempotencyKey);
    mutationLock.current = true;
    setAdjustingWallet(true);
    try {
      const action = readWalletAction(adjustmentStorageKey) || { key: idempotencyKey, payload: {
        site_id: account.site_id,
        amount,
        reason: walletAdjustmentReason.trim(),
      } };
      saveWalletAction(adjustmentStorageKey, action);
      setAdjustmentPending(true);
      await notificationCenterService.createAdminAlimTalkWalletAdjustment(action.payload as { site_id: number; amount: number; reason: string }, action.key);
      clearWalletAction(adjustmentStorageKey);
      setAdjustmentPending(false);
      toast.success("지갑 잔액을 조정했습니다.");
      setWalletAdjustmentOpen(false);
      setWalletAdjustmentAmount("");
      setWalletAdjustmentReason("");
      setWalletAdjustmentIdempotencyKey("");
      await load();
    } catch (error: any) {
      if (isDefinitiveWalletFailure(error)) { clearWalletAction(adjustmentStorageKey); setAdjustmentPending(false); }
      toast.error(errorMessage(error, "지갑 잔액을 조정하지 못했습니다."));
    } finally {
      mutationLock.current = false;
      setAdjustingWallet(false);
    }
  };

  const submitAdminRate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canManageHeadquarters || !account) return;
    const unitPrice = Number(rateUnitPrice);
    if (!Number.isInteger(unitPrice) || unitPrice <= 0 || !rateEffectiveFrom || !rateReason.trim()) {
      toast.error("단가, 적용 시작일과 변경 사유를 확인해 주세요.");
      return;
    }
    setSavingAdminRate(true);
    try {
      await notificationCenterService.createAdminAlimTalkMessageRate({
        site: rateScope === "SITE" ? account.site_id : null,
        channel: rateChannel,
        source: null,
        unit_price: unitPrice,
        currency: "KRW",
        is_active: true,
        effective_from: rateEffectiveFrom,
        reason: rateReason.trim(),
      });
      toast.success("알림톡 단가를 등록했습니다.");
      setRateUnitPrice("");
      setRateReason("");
      await load();
    } catch (error: any) {
      toast.error(errorMessage(error, "알림톡 단가를 등록하지 못했습니다."));
    } finally {
      setSavingAdminRate(false);
    }
  };

  const walletCurrency = wallet?.currency || "KRW";
  const availableBalance = wallet?.available_balance ?? wallet?.balance ?? 0;
  const reservedBalance = wallet?.reserved_balance ?? 0;
  const modeCards = useMemo(() => [
    { value: "HEADQUARTERS" as const, title: "본사 공용 계정", description: "본사 발신 프로필·템플릿과 지점 충전금을 사용합니다." },
    { value: "DIRECT" as const, title: "지점 직접 계정", description: "지점에서 발급받은 발신 프로필과 템플릿을 사용합니다." },
  ], []);

  const openWalletAdjustment = () => {
    if (!canManageHeadquarters) return;
    if (adjustmentPending) { setWalletAdjustmentOpen(true); return; }
    setWalletAdjustmentAmount("");
    setWalletAdjustmentReason("");
    setWalletAdjustmentIdempotencyKey(createWalletAdjustmentIdempotencyKey());
    setWalletAdjustmentOpen(true);
  };

  const closeWalletAdjustment = () => {
    if (adjustingWallet) return;
    setWalletAdjustmentOpen(false);
    if (!adjustmentPending) setWalletAdjustmentIdempotencyKey("");
  };

  if (loading && !account) return <div className="text-center py-4"><Spinner size="sm" className="me-2" />채널 정보를 확인하고 있습니다.</div>;
  if (!account) return <Alert color="danger" className="d-flex justify-content-between align-items-center"><span>알림톡 채널 정보를 불러오지 못했습니다.</span><Button size="sm" color="outline-danger" onClick={() => void load()}>다시 시도</Button></Alert>;

  return (
    <div>
      <div className="mb-3">
        <h5 className="mb-1">알림톡 계정 사용 방식</h5>
        <p className="text-muted mb-0">현재 지점이 사용할 발신 계정과 비용 처리 방식을 확인합니다.</p>
      </div>
      <Row className="g-3 mb-4">
        {modeCards.map((item) => (
          <Col md={6} key={item.value}>
            <button
              type="button"
              className={`w-100 h-100 text-start rounded border p-3 bg-white ${mode === item.value ? "border-primary" : ""}`}
              onClick={() => { if (canChangeMode) void changeMode(item.value); }}
              disabled={!canChangeMode || changingMode || adjustingWallet || submittingTopup || reviewing || adjustmentPending || topupPending}
              aria-pressed={mode === item.value}
            >
              <div className="d-flex justify-content-between gap-2"><strong>{item.title}</strong>{mode === item.value ? <Badge color="primary">사용 중</Badge> : null}</div>
              <div className="text-muted small mt-2">{item.description}</div>
            </button>
          </Col>
        ))}
      </Row>
      {!canChangeMode ? <div className="form-text mb-3">계정 사용 방식 변경은 HQ 관리자 페이지에서 할 수 있습니다.</div> : null}
      {!isHeadquartersView && !canManageSiteWallet && isSiteManager ? (
        <Alert color="info">매니저는 알림톡 연결 상태와 잔액 요약을 조회할 수 있습니다. 계정 연결과 충전 요청 관리는 지점 최고 관리자에게 문의해 주세요.</Alert>
      ) : null}

      {!isHeadquartersView && mode === "DIRECT" ? <AlimTalkAccountSettings readOnly={!canEditConnection} /> : null}

      {mode === "HEADQUARTERS" ? (
        <div className="border rounded p-3 p-md-4">
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
            <div><h5 className="mb-1">알림톡 충전금</h5><p className="text-muted mb-0">무통장 입금 후 본사 관리자가 확인한 금액이 반영됩니다.</p></div>
            <div className="d-flex flex-wrap gap-2">
              {canManageHeadquarters ? <Button color="soft-secondary" disabled={loading || changingMode || reviewing} onClick={openWalletAdjustment}>지갑 수동 조정</Button> : null}
              {canRequestTopup ? <Button color="primary" onClick={() => setTopupOpen(true)} disabled={loading || loadingTopups || topupsUnavailable || (Boolean(pendingTopup) && !topupPending)}>충전 요청</Button> : null}
            </div>
          </div>
          <Row className="g-2 mb-3">
            <Col md={4}><div className="rounded border bg-light p-3 h-100"><div className="text-muted small">사용 가능 잔액</div><strong className="fs-5">{formatMoney(availableBalance, walletCurrency)}</strong></div></Col>
            <Col md={4}><div className="rounded border bg-light p-3 h-100"><div className="text-muted small">발송 예약 금액</div><strong className="fs-5">{formatMoney(reservedBalance, walletCurrency)}</strong></div></Col>
            <Col md={4}><div className="rounded border bg-light p-3 h-100"><div className="text-muted small">입금 확인 대기</div><strong className="fs-5">{wallet?.pending_topup_count || topups.filter((request) => String(request.status).toUpperCase() === "PENDING").length}건</strong></div></Col>
          </Row>
          {billingSetting?.bank_name || billingSetting?.account_number ? <Alert color="info"><strong>입금 계좌</strong> · {[billingSetting.bank_name, billingSetting.account_number, billingSetting.account_holder].filter(Boolean).join(" / ")}{billingSetting.guidance ? <div className="mt-1">{billingSetting.guidance}</div> : null}</Alert> : null}
          {walletUnavailable ? <Alert color="warning">충전금 API가 아직 준비되지 않아 계정 응답의 잔액만 표시합니다.</Alert> : null}
          {pendingTopup ? <Alert color="info">처리 대기 중인 충전 요청이 있어 완료 또는 취소 후 새 요청을 등록할 수 있습니다.</Alert> : null}
          {topupsUnavailable ? <Alert color="warning">충전 요청 내역을 불러올 수 없습니다. <Button size="sm" onClick={() => void load()}>다시 조회</Button></Alert> : null}
          {!isHeadquartersView && canManageSiteWallet ? (
            <>{topupFilters}<div className="table-responsive border rounded">
              <Table className="align-middle mb-0 small">
                <thead><tr><th>요청일</th><th>금액</th><th>입금자명</th><th>상태</th><th>메모</th><th /></tr></thead>
                <tbody>
                  {loadingTopups ? <tr><td colSpan={6}><Spinner size="sm" /></td></tr> : null}
                  {!topupsUnavailable && !loadingTopups && topups.length === 0 ? <tr><td colSpan={6} className="text-center text-muted py-3">충전 요청 내역이 없습니다.</td></tr> : null}
                  {topups.map((request) => {
                    const status = statusMeta(request.status);
                    return <tr key={request.id}><td>요청 #{request.id}<br />{request.created_at ? new Date(request.created_at).toLocaleString("ko-KR") : "-"}</td><td>{formatMoney(amountOf(request), walletCurrency)}</td><td>{request.depositor_name || "-"}</td><td><Badge color={status.color}>{status.label}</Badge></td><td>{request.rejection_reason || request.review_memo || request.memo || request.note || "-"}{request.wallet_entry_id ? <div>원장 #{request.wallet_entry_id}</div> : null}</td><td className="text-end">{String(request.status).toLowerCase() === "pending" ? <Button size="sm" color="outline-danger" disabled={cancellingTopup || submittingTopup} onClick={() => void cancelTopup(request)}>요청 취소</Button> : null}</td></tr>;
                  })}
                </tbody>
              </Table>
            </div>{topupPagination}<AlimTalkLedger refreshKey={refreshKey} /></>
          ) : null}
        </div>
      ) : null}

      {canManageHeadquarters ? (
        <div className="border rounded p-3 p-md-4 mt-4">
          <div className="mb-3">
            <h5 className="mb-1">본사 청구 설정</h5>
            <p className="text-muted mb-0">지점 충전 요청에 안내할 무통장 입금 계좌와 잔액 차감 적용 여부를 관리합니다.</p>
          </div>
          {adminBillingUnavailable || !adminBillingSetting ? (
            <Alert color="warning" className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-0">
              <span>본사 청구 설정 API를 사용할 수 없습니다.</span>
              <Button color="outline-warning" size="sm" onClick={() => void load()}>다시 시도</Button>
            </Alert>
          ) : (
            <form onSubmit={submitAdminBilling}>
              <Row className="g-3">
                <Col md={4}>
                  <Label htmlFor="admin-billing-bank-name">입금 은행</Label>
                  <Input id="admin-billing-bank-name" value={adminBillingSetting.bank_name || ""} onChange={(event) => updateAdminBillingField("bank_name", event.target.value)} />
                </Col>
                <Col md={4}>
                  <Label htmlFor="admin-billing-account-number">계좌번호</Label>
                  <Input id="admin-billing-account-number" value={adminBillingSetting.account_number || ""} onChange={(event) => updateAdminBillingField("account_number", event.target.value)} />
                </Col>
                <Col md={4}>
                  <Label htmlFor="admin-billing-account-holder">예금주</Label>
                  <Input id="admin-billing-account-holder" value={adminBillingSetting.account_holder || ""} onChange={(event) => updateAdminBillingField("account_holder", event.target.value)} />
                </Col>
                <Col xs={12}>
                  <Label htmlFor="admin-billing-guidance">입금 안내</Label>
                  <Input id="admin-billing-guidance" type="textarea" rows={3} value={adminBillingSetting.guidance || ""} onChange={(event) => updateAdminBillingField("guidance", event.target.value)} />
                </Col>
                <Col xs={12}>
                  <div className="form-check">
                    <Input id="admin-billing-enforcement" className="form-check-input" type="checkbox" checked={Boolean(adminBillingSetting.enforcement_enabled)} onChange={(event) => updateAdminBillingField("enforcement_enabled", event.target.checked)} />
                    <Label htmlFor="admin-billing-enforcement" className="form-check-label">본사 계정 발송 시 충전금 잔액 검사를 적용</Label>
                  </div>
                </Col>
              </Row>
              <div className="d-flex justify-content-end mt-3">
                <Button type="submit" color="primary" disabled={savingAdminBilling}>{savingAdminBilling ? <><Spinner size="sm" className="me-2" />저장 중</> : "청구 설정 저장"}</Button>
              </div>
            </form>
          )}
        </div>
      ) : null}

      {canManageHeadquarters ? (
        <div className="border rounded p-3 p-md-4 mt-4">
          <div className="mb-3">
            <h5 className="mb-1">알림톡 단가 관리</h5>
            <p className="text-muted mb-0">공용 기본 단가 또는 현재 지점 우선 단가를 등록합니다. 새 단가는 적용일과 출처 우선순위에 따라 발송 시 선택됩니다.</p>
          </div>
          {adminRatesUnavailable ? <Alert color="warning">알림톡 단가 목록을 불러오지 못했습니다.</Alert> : null}
          <form onSubmit={submitAdminRate} className="mb-3">
            <Row className="g-3 align-items-end">
              <Col md={3}>
                <Label htmlFor="alimtalk-rate-scope">적용 범위</Label>
                <Input id="alimtalk-rate-scope" type="select" value={rateScope} onChange={(event) => setRateScope(event.target.value as "GLOBAL" | "SITE")}>
                  <option value="GLOBAL">공용 기본</option>
                  <option value="SITE">현재 지점 우선</option>
                </Input>
              </Col>
              <Col md={2}>
                <Label htmlFor="alimtalk-rate-channel">유형</Label>
                <Input id="alimtalk-rate-channel" type="select" value={rateChannel} onChange={(event) => setRateChannel(event.target.value as "AT" | "AI")}>
                  <option value="AT">알림톡</option>
                  <option value="AI">이미지 알림톡</option>
                </Input>
              </Col>
              <Col md={2}>
                <Label htmlFor="alimtalk-rate-unit-price">건당 단가</Label>
                <Input id="alimtalk-rate-unit-price" type="number" min={1} step={1} value={rateUnitPrice} onChange={(event) => setRateUnitPrice(event.target.value)} required />
              </Col>
              <Col md={2}>
                <Label htmlFor="alimtalk-rate-effective-from">적용 시작일</Label>
                <Input id="alimtalk-rate-effective-from" type="date" value={rateEffectiveFrom} onChange={(event) => setRateEffectiveFrom(event.target.value)} required />
              </Col>
              <Col md={3}>
                <Label htmlFor="alimtalk-rate-reason">등록 사유</Label>
                <Input id="alimtalk-rate-reason" value={rateReason} onChange={(event) => setRateReason(event.target.value)} placeholder="예: 2026년 공급 단가" required />
              </Col>
            </Row>
            <div className="d-flex justify-content-end mt-3">
              <Button type="submit" color="primary" disabled={savingAdminRate}>{savingAdminRate ? <><Spinner size="sm" className="me-2" />등록 중</> : "단가 등록"}</Button>
            </div>
          </form>
          <div className="table-responsive border rounded">
            <Table className="align-middle mb-0 small">
              <thead><tr><th>범위</th><th>유형</th><th>단가</th><th>적용 기간</th><th>사유</th><th>상태</th></tr></thead>
              <tbody>
                {adminRates.length === 0 ? <tr><td colSpan={6} className="text-center text-muted py-3">등록된 알림톡 단가가 없습니다.</td></tr> : null}
                {adminRates.map((rate) => (
                  <tr key={rate.id}>
                    <td>{rate.site ? rate.site_name || `지점 ${rate.site}` : "공용 기본"}</td>
                    <td>{rate.channel === "AI" ? "이미지 알림톡" : "알림톡"}</td>
                    <td>{formatMoney(rate.unit_price, rate.currency)}</td>
                    <td>{rate.effective_from}{rate.effective_to ? ` ~ ${rate.effective_to}` : " 이후"}</td>
                    <td>{rate.reason || "-"}</td>
                    <td><Badge color={rate.is_active ? "success" : "secondary"}>{rate.is_active ? "사용" : "중지"}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      ) : null}

      {canManageHeadquarters ? (
        <div className="border rounded p-3 p-md-4 mt-4">
          <div className="d-flex flex-wrap justify-content-between gap-2 mb-3"><div><h5 className="mb-1">본사 입금 확인</h5><p className="text-muted mb-0">입금 내역을 직접 확인한 뒤 실제 확인 금액을 충전합니다.</p></div><Button color="soft-primary" size="sm" onClick={() => void load()}>새로고침</Button></div>
          {topupFilters}
          <div className="table-responsive border rounded">
            <Table className="align-middle mb-0 small">
              <thead><tr><th>지점</th><th>요청일</th><th>요청 금액</th><th>입금자명</th><th>요청 메모</th><th className="text-end">처리</th></tr></thead>
              <tbody>
                {loadingTopups ? <tr><td colSpan={6}><Spinner size="sm" /></td></tr> : null}
                {adminTopupsUnavailable ? <tr><td colSpan={6} className="text-danger py-3">충전 요청 조회에 실패했습니다. 새로고침해 주세요.</td></tr> : null}
                {!adminTopupsUnavailable && !loadingTopups && adminTopups.length === 0 ? <tr><td colSpan={6} className="text-center text-muted py-3">조회된 충전 요청이 없습니다.</td></tr> : null}
                {adminTopups.map((request) => <tr key={request.id}><td>{request.site_name || request.site_id || "-"}</td><td>요청 #{request.id}<br />{request.created_at ? new Date(request.created_at).toLocaleString("ko-KR") : "-"}</td><td>{formatMoney(amountOf(request))}</td><td>{request.depositor_name || "-"}</td><td>{request.rejection_reason || request.review_memo || request.memo || request.note || "-"}{request.wallet_entry_id ? <div>원장 #{request.wallet_entry_id}</div> : null}</td><td><Badge color={statusMeta(request.status).color}>{statusMeta(request.status).label}</Badge>{request.approved_amount != null ? <div>승인 {formatMoney(request.approved_amount)}</div> : null}{request.reviewed_at ? <small>{new Date(request.reviewed_at).toLocaleString("ko-KR")}</small> : null}{String(request.status).toUpperCase() === "PENDING" ? <div className="d-flex justify-content-end gap-1"><Button size="sm" color="success" disabled={reviewing || adjustingWallet} onClick={() => openReview(request, "approve")}>입금 확인</Button><Button size="sm" color="outline-danger" disabled={reviewing || adjustingWallet} onClick={() => openReview(request, "reject")}>반려</Button></div> : null}</td></tr>)}
              </tbody>
            </Table>
          </div>{topupPagination}
        </div>
      ) : null}

      <Modal isOpen={!isHeadquartersView && topupOpen} onClose={() => { if (!submittingTopup) setTopupOpen(false); }} variant="dialog" size="lg">
        <ModalHeader toggle={() => { if (!submittingTopup) setTopupOpen(false); }}>알림톡 충전 요청</ModalHeader>
        <form onSubmit={submitTopup}>
          <ModalBody>
            {topupPending ? <Alert color="warning">처리 결과 확인이 필요합니다. 같은 요청으로 다시 확인해 주세요.</Alert> : null}
            <Alert color="info">요청 등록 후 안내된 계좌로 입금해 주세요. 본사 관리자가 입금을 확인한 뒤 충전금이 반영됩니다.</Alert>
            <div className="mb-3"><Label htmlFor="topup-amount">요청 금액</Label><Input disabled={submittingTopup || topupPending} id="topup-amount" type="number" min={1} step={1} value={topupAmount} onChange={(event) => setTopupAmount(event.target.value)} placeholder="예: 100000" required /></div>
            {billingSetting?.bank_name || billingSetting?.account_number ? <Alert color="light"><strong>입금 계좌</strong> · {[billingSetting.bank_name, billingSetting.account_number, billingSetting.account_holder].filter(Boolean).join(" / ")}{billingSetting.guidance ? <div className="mt-1">{billingSetting.guidance}</div> : null}</Alert> : null}
            <div className="mb-3"><Label htmlFor="topup-depositor">입금자명</Label><Input disabled={submittingTopup || topupPending} id="topup-depositor" value={depositorName} onChange={(event) => setDepositorName(event.target.value)} required /></div>
            <div className="mb-3"><Label htmlFor="topup-date">입금 예정일</Label><Input disabled={submittingTopup || topupPending} id="topup-date" type="date" value={expectedDepositDate} onChange={(event) => setExpectedDepositDate(event.target.value)} /></div>
            <div><Label htmlFor="topup-note">요청 메모</Label><Input disabled={submittingTopup || topupPending} id="topup-note" type="textarea" rows={3} value={topupNote} onChange={(event) => setTopupNote(event.target.value)} /></div>
          </ModalBody>
          <ModalFooter><Button type="button" color="secondary" onClick={() => setTopupOpen(false)} disabled={submittingTopup}>취소</Button><Button type="submit" color="primary" disabled={submittingTopup}>{submittingTopup ? <><Spinner size="sm" className="me-2" />요청 중</> : "충전 요청"}</Button></ModalFooter>
        </form>
      </Modal>

      <Modal isOpen={canManageHeadquarters && Boolean(review)} onClose={() => { if (!reviewing) setReview(null); }} variant="dialog" size="lg">
        <ModalHeader toggle={() => { if (!reviewing) setReview(null); }}>{review?.action === "approve" ? "입금 확인 및 충전" : "충전 요청 반려"}</ModalHeader>
        <form onSubmit={submitReview}>
          <ModalBody>
            {review?.action === "approve" ? <div className="mb-3"><Label htmlFor="review-amount">실제 입금 확인 금액</Label><Input disabled={reviewing} id="review-amount" type="number" min={1} step={1} value={reviewAmount} onChange={(event) => setReviewAmount(event.target.value)} required /></div> : null}
            <div><Label htmlFor="review-memo">{review?.action === "approve" ? "확인 메모" : "반려 사유"}</Label><Input disabled={reviewing} id="review-memo" type="textarea" rows={3} value={reviewMemo} onChange={(event) => setReviewMemo(event.target.value)} required={review?.action === "reject"} /></div>
          </ModalBody>
          <ModalFooter><Button type="button" color="secondary" onClick={() => setReview(null)} disabled={reviewing}>취소</Button><Button type="submit" color={review?.action === "approve" ? "success" : "danger"} disabled={reviewing}>{reviewing ? <><Spinner size="sm" className="me-2" />처리 중</> : review?.action === "approve" ? "충전 반영" : "반려"}</Button></ModalFooter>
        </form>
      </Modal>

      <Modal isOpen={canManageHeadquarters && mode === "HEADQUARTERS" && walletAdjustmentOpen} onClose={closeWalletAdjustment} variant="dialog" size="lg">
        <ModalHeader toggle={closeWalletAdjustment}>알림톡 지갑 수동 조정</ModalHeader>
        <form onSubmit={submitWalletAdjustment}>
          <ModalBody>
            {adjustmentPending ? <Alert color="warning">처리 결과 확인이 필요합니다. 같은 금액과 사유로 다시 확인해 주세요.</Alert> : null}
            <Alert color="warning">조정 금액은 즉시 지갑에 반영됩니다. 충전은 양수, 차감은 음수로 입력하고 감사 가능한 사유를 남겨 주세요.</Alert>
            <div className="mb-3">
              <Label htmlFor="wallet-adjustment-amount">조정 금액</Label>
              <Input disabled={adjustingWallet || adjustmentPending} id="wallet-adjustment-amount" type="number" step={1} value={walletAdjustmentAmount} onChange={(event) => { setWalletAdjustmentAmount(event.target.value); setWalletAdjustmentIdempotencyKey(createWalletAdjustmentIdempotencyKey()); }} placeholder="예: 50000 또는 -10000" required />
            </div>
            <div>
              <Label htmlFor="wallet-adjustment-reason">조정 사유</Label>
              <Input disabled={adjustingWallet || adjustmentPending} id="wallet-adjustment-reason" type="textarea" rows={3} value={walletAdjustmentReason} onChange={(event) => { setWalletAdjustmentReason(event.target.value); setWalletAdjustmentIdempotencyKey(createWalletAdjustmentIdempotencyKey()); }} required />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button type="button" color="secondary" onClick={closeWalletAdjustment} disabled={adjustingWallet}>취소</Button>
            <Button type="submit" color="primary" disabled={adjustingWallet}>{adjustingWallet ? <><Spinner size="sm" className="me-2" />반영 중</> : "잔액 조정"}</Button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  );
}
